import React, { useState, useEffect, useRef } from 'react';
import { staffClient } from 'clients/staff';
import { getS3UploadUrl, uploadS3File } from 'utils/s3';
import Button from 'ui/Button';
import Loading from 'ui/Loading';
import Section from 'ui/Section';
import { Switch } from 'ui/shadcn';
import { cn } from 'lib/utils';
import { toast } from 'react-toastify';
import { ChevronDownIcon, ChevronUpIcon, PhotoIcon, PlusIcon } from '@heroicons/react/24/outline';

interface MainImage {
    id: number;
    image_url: string;
    title: string;
    display_order: number;
    is_active: boolean;
}

const MainImageManager: React.FC = () => {
    const [images, setImages] = useState<MainImage[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        fetchImages();
    }, []);

    const fetchImages = async () => {
        try {
            const res = await staffClient.get('/website-image');
            setImages(res.data);
        } catch (error) {
            console.error('Failed to fetch images:', error);
            toast.error('이미지 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setUploading(true);
        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                if (!file.type.startsWith('image/')) {
                    toast.error(`${file.name}은 이미지 파일이 아닙니다.`);
                    continue;
                }
                const signedUrl = await getS3UploadUrl(file);
                if (!signedUrl) throw new Error('S3 URL 생성 실패');
                const imageUrl = await uploadS3File(file, signedUrl);
                if (!imageUrl) throw new Error('S3 업로드 실패');

                await staffClient.post('/website-image', {
                    image_url: imageUrl,
                    title: '',
                    is_active: true,
                });
            }
            toast.success(`${files.length}개 이미지가 추가되었습니다.`);
            fetchImages();
        } catch (error) {
            console.error('Upload failed:', error);
            toast.error('이미지 업로드에 실패했습니다.');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleToggleActive = async (image: MainImage) => {
        try {
            const newActive = !image.is_active;
            await staffClient.patch(`/website-image/${image.id}`, { is_active: newActive });
            setImages(prev => prev.map(img => img.id === image.id ? { ...img, is_active: newActive } : img));
            toast.success(newActive ? '이미지가 활성화되었습니다.' : '이미지가 비활성화되었습니다.');
        } catch (error) {
            toast.error('상태 변경에 실패했습니다.');
        }
    };

    const handleTitleChange = async (image: MainImage, title: string) => {
        try {
            await staffClient.patch(`/website-image/${image.id}`, { title });
            setImages(prev => prev.map(img => img.id === image.id ? { ...img, title } : img));
        } catch (error) {
            toast.error('제목 저장에 실패했습니다.');
        }
    };

    const handleDelete = async (image: MainImage) => {
        if (!window.confirm('이 이미지를 삭제하시겠습니까?')) return;
        try {
            await staffClient.delete(`/website-image/${image.id}`);
            setImages(prev => prev.filter(img => img.id !== image.id));
            toast.success('이미지가 삭제되었습니다.');
        } catch (error) {
            toast.error('삭제에 실패했습니다.');
        }
    };

    const handleMove = async (index: number, direction: 'up' | 'down') => {
        const newImages = [...images];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newImages.length) return;

        [newImages[index], newImages[targetIndex]] = [newImages[targetIndex], newImages[index]];

        const orderData = newImages.map((img, i) => ({ id: img.id, display_order: i }));
        setImages(newImages);

        try {
            await staffClient.patch('/website-image/order', orderData);
        } catch (error) {
            toast.error('순서 변경에 실패했습니다.');
            fetchImages();
        }
    };

    const activeCount = images.filter(img => img.is_active).length;

    return (
        <Section
            className="overflow-hidden"
            title={
                <>
                    메인 이미지
                    {!loading && (
                        <span className="text-xs font-normal text-muted-foreground tabular-nums">
                            총 <span className="font-medium text-foreground">{images.length}</span>건 · 활성 {activeCount}건
                        </span>
                    )}
                </>
            }
            description="홈페이지 메인에 슬라이드로 표시될 이미지를 관리합니다. 권장 크기 1920 x 800px, 순서는 화살표로 변경할 수 있습니다."
            actions={
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleUpload}
                    />
                    <Button
                        variant="primary"
                        icon={!uploading ? <PlusIcon /> : undefined}
                        loading={uploading}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {uploading ? '업로드 중...' : '이미지 추가'}
                    </Button>
                </>
            }
            flush
        >
            {loading ? (
                <Loading text="이미지를 불러오는 중..." />
            ) : images.length === 0 ? (
                <div className="p-4">
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full rounded border border-dashed border-neutral-300 py-16 text-center transition-colors duration-fast hover:border-navy-700 hover:bg-navy-50"
                    >
                        <PhotoIcon className="mx-auto mb-3 h-10 w-10 text-neutral-400" strokeWidth={1.5} />
                        <p className="text-md font-medium text-neutral-700">클릭하여 이미지를 추가하세요</p>
                        <p className="mt-1 text-xs text-muted-foreground">여러 장을 한 번에 선택할 수 있습니다</p>
                    </button>
                </div>
            ) : (
                <ul className="divide-y divide-border">
                    {images.map((image, index) => (
                        <li
                            key={image.id}
                            className={cn(
                                'flex items-center gap-4 px-4 py-3 transition-colors duration-fast',
                                !image.is_active && 'bg-neutral-50'
                            )}
                        >
                            {/* 순서 번호 + 화살표 */}
                            <div className="flex w-8 shrink-0 flex-col items-center gap-0.5">
                                <button
                                    type="button"
                                    onClick={() => handleMove(index, 'up')}
                                    disabled={index === 0}
                                    className="rounded p-0.5 text-neutral-400 hover:bg-muted hover:text-neutral-700 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                                    aria-label="위로"
                                >
                                    <ChevronUpIcon className="h-4 w-4" />
                                </button>
                                <span className="text-xs font-semibold tabular-nums text-neutral-500">{index + 1}</span>
                                <button
                                    type="button"
                                    onClick={() => handleMove(index, 'down')}
                                    disabled={index === images.length - 1}
                                    className="rounded p-0.5 text-neutral-400 hover:bg-muted hover:text-neutral-700 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                                    aria-label="아래로"
                                >
                                    <ChevronDownIcon className="h-4 w-4" />
                                </button>
                            </div>

                            {/* 이미지 미리보기 */}
                            <div
                                className={cn(
                                    'h-20 w-48 shrink-0 overflow-hidden rounded border border-border bg-neutral-100',
                                    !image.is_active && 'opacity-60'
                                )}
                            >
                                <img
                                    src={image.image_url}
                                    alt={image.title || '메인 이미지'}
                                    className="h-full w-full object-cover"
                                />
                            </div>

                            {/* 제목 입력 */}
                            <div className="min-w-0 flex-1">
                                <input
                                    type="text"
                                    value={image.title || ''}
                                    onChange={(e) => setImages(prev => prev.map(img => img.id === image.id ? { ...img, title: e.target.value } : img))}
                                    onBlur={(e) => handleTitleChange(image, e.target.value)}
                                    placeholder="이미지 설명 (선택사항)"
                                    className="h-9 w-full rounded border border-input bg-background px-3 text-base text-foreground placeholder:text-neutral-400 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                                />
                                <p className="mt-1 truncate text-xs text-neutral-400">{image.image_url}</p>
                            </div>

                            {/* 활성/비활성 토글 */}
                            <div className="flex w-20 shrink-0 items-center justify-end gap-2">
                                <span className={cn('text-xs', image.is_active ? 'text-foreground' : 'text-muted-foreground')}>
                                    {image.is_active ? '활성' : '비활성'}
                                </span>
                                <Switch
                                    checked={image.is_active}
                                    onCheckedChange={() => handleToggleActive(image)}
                                    aria-label="활성 여부"
                                />
                            </div>

                            {/* 삭제 */}
                            <Button
                                size="small"
                                variant="danger-outline"
                                className="shrink-0"
                                onClick={() => handleDelete(image)}
                            >
                                삭제
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
        </Section>
    );
};

export default MainImageManager;
